import React, { useState } from "react";
import { Box, Typography, Button, TextField, CircularProgress } from "@mui/material";
import { Slack } from 'lucide-react';
import axios from "axios";
import { supabase } from "../../supabaseClient";
import { API_URL } from "../../globals";

const formatChannelName = (companyName) => {
  // Remove special characters and whitespace, convert to lowercase
  return companyName.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
};

const JoinSlackButton = ({ onComplete, email: initialEmail, channelName, isModular = false }) => {
  const [showEmailField, setShowEmailField] = useState(false);
  const [email, setEmail] = useState(initialEmail || '');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSendInvite = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const authToken = (await supabase.auth.getSession()).data.session.access_token;
      const headers = {
        Authorization: `Bearer ${authToken}`,
      };

      const response = await axios.post(
        `${API_URL}/slack_support/generate_invite`,
        {
          channel_name: channelName,
          email: email
        },
        { headers }
      );

      if (response.data.success) {
        const channelLink = response.data.channel_link;
        
        // First move to next step
        if (onComplete) {
          onComplete();
        }
        
        // Then open the channel link in a new tab after a small delay
        setTimeout(() => {
          const newWindow = window.open(channelLink, '_blank', 'noopener,noreferrer');
          if (!newWindow) {
            // Fallback: create a clickable link
            setError(
              <span>
                Popup blocked. Please{' '}
                <a href={channelLink} target="_blank" rel="noopener noreferrer" style={{ color: '#4A154B' }}>
                  click here
                </a>
                {' '}to join the Slack channel.
              </span>
            );
          }
        }, 100);
      } else {
        setError('Sorry, an error occurred');
      }
    } catch (error) {
      console.error('Error generating Slack invite:', error);
      setError('Sorry, an error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ width: isModular ? 'auto' : '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      {!showEmailField ? (
        <Button
          onClick={() => setShowEmailField(true)}
          startIcon={<Slack size={20} color="#fff" />}
          sx={{
            height: 56,
            backgroundColor: '#4A154B',
            color: '#fff',
            textTransform: 'none',
            px: 4,
            borderRadius: 2,
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
            fontWeight: 600,
            fontSize: "1.1rem",
            '&:hover': {
              backgroundColor: '#611f69',
              transform: "translateY(-2px)",
            }
          }}
        >
          Join Dedicated Channel
        </Button>
      ) : (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: isModular ? 'row' : 'column', gap: 2 }}>
          <TextField
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            type="email"
            disabled={isLoading}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: 2,
                fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#4A154B',
                },
              },
              '& .MuiInputBase-input': {
                '&::placeholder': {
                  color: '#94a3b8',
                  opacity: 1,
                },
              },
            }}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: isModular ? '140px' : 'auto' }}>
            <Button
              onClick={handleSendInvite}
              disabled={isLoading}
              sx={{
                backgroundColor: '#4A154B',
                color: '#fff',
                textTransform: 'none',
                px: 3,
                py: 1.5,
                minWidth: '140px',
                whiteSpace: 'nowrap',
                borderRadius: 2,
                fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#611f69'
                },
                '&.Mui-disabled': {
                  backgroundColor: '#2A0F2B',
                  color: '#888'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {isLoading ? (
                  <CircularProgress size={20} sx={{ color: '#fff' }} />
                ) : (
                  <Slack size={20} color="#fff" />
                )}
                {isLoading ? 'Sending...' : 'Send Invite'}
              </Box>
            </Button>
            {error && (
              <Typography 
                sx={{ 
                  color: '#f44336',
                  fontSize: '0.75rem',
                  textAlign: 'center'
                }}
              >
                {error}
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const SlackSupportStep = ({ onComplete, userData }) => {
  return (
    <Box 
      sx={{ 
        width: "100%",
        minHeight: "70vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        py: 8,
      }}
    >
      <Box
        sx={{
          textAlign: "center",
          maxWidth: "600px",
          mx: "auto",
          px: 3,
          width: "100%",
        }}
      >
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: 700,
            color: "#ffffff",
            mb: 3,
            letterSpacing: "-0.02em",
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
          }}
        >
          🔔 Join Your Channel
        </Typography>
        
        <Typography
          variant="h6"
          sx={{
            color: "#94a3b8",
            fontWeight: 400,
            mb: 8,
            lineHeight: 1.6,
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
          }}
        >
          Join your dedicated Slack channel to receive notifications on test runs. You will get notified of the test results with no set up and receive 24/7 support from the team.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' }}>
          <JoinSlackButton 
            onComplete={onComplete}
            email={userData?.email}
            channelName={formatChannelName(userData?.companyName || '')}
          />
          
          {process.env.REACT_APP_ENV === 'development' && (
            <Button
              onClick={onComplete}
              variant="outlined"
              sx={{
                color: "#94a3b8",
                borderColor: "rgba(148, 163, 184, 0.3)",
                textTransform: 'none',
                px: 3,
                py: 1,
                borderRadius: 2,
                fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                fontWeight: 500,
                '&:hover': {
                  borderColor: "rgba(148, 163, 184, 0.5)",
                  backgroundColor: "rgba(148, 163, 184, 0.05)",
                }
              }}
            >
              Skip for now
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default SlackSupportStep; 